// Championship quests, and the goal a player can pin from their profile.
//
// The profile page shows every quest's progress. A player can also pin one quest or
// achievement as their goal (prefs.activeGoal), and the bracket page shows it in a small
// widget from prefs.goalInfo: a saved copy ({ name, remaining, status }) that spares the
// bracket page the 7 MB player index on every visit. Both pages build that copy here,
// from the same data, so they can't disagree. Pure: the caller supplies the player and
// the class lists (see playerHelpers buildClassAgg / monsterClasses / humanClasses).

export const ZODIAC_SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces', 'Serpentarius'];

// Every quest's progress for one player (a playerFromRecord object). classAgg holds
// [male, female] championship counts per class across the whole index, which is what
// says a class can roll a gender at all. carried = carriedByMonsters(), or null while
// champHistory is still loading.
export function playerQuests(player, { monsterClasses, humanClasses, classAgg, carried }) {
  if (!player || !player.champClasses) return [];
  const cc = player.champClasses, cs = player.champSigns || {};
  const mon = monsterClasses, hum = humanClasses, agg = classAgg;
  const canM = (c) => agg[c] && agg[c][0] > 0, canF = (c) => agg[c] && agg[c][1] > 0;
  // Count how many targets the player has championed, how many full sets, and what's still needed.
  // `remaining` = targets never championed (for first completion).
  // `remainingNext` = targets at the minimum count (for the next full set, once all done once).
  const calc = (targets, countFn, labelFn) => {
    let championed = 0, minC = Infinity;
    const remaining = [], counts = [];
    for (const t of targets) {
      const c = countFn(t);
      counts.push({ t, c });
      if (c > 0) championed++; else remaining.push(labelFn(t));
      if (c < minC) minC = c;
    }
    const completions = championed === targets.length ? minC : 0;
    // For repeat completionists: what's needed for the next set
    const remainingNext = completions > 0 ? counts.filter((x) => x.c === minC).map((x) => labelFn(x.t)) : [];
    return { championed, total: targets.length, completions, pct: Math.round(100 * championed / (targets.length || 1)), remaining, remainingNext };
  };
  const classCount = (cls) => (cc[cls] && cc[cls][0]) || 0;
  const genderCount = (cls, idx) => (cc[cls] && cc[cls][idx]) || 0;
  const gendered = hum.flatMap((c) => [canM(c) ? { cls: c, idx: 1 } : null, canF(c) ? { cls: c, idx: 2 } : null].filter(Boolean));
  const gLabel = (t) => t.cls + (t.idx === 1 ? ' ♂' : t.idx === 2 ? ' ♀' : '');
  // All-Male / All-Female are now sub-progress inside the Human Gendered card (mirrors the leaderboard's ♂/♀ sub-tabs).
  const maleProg = calc(hum.filter(canM), (cls) => genderCount(cls, 1), (c) => c);
  const femaleProg = calc(hum.filter(canF), (cls) => genderCount(cls, 2), (c) => c);
  const car = carried || {};
  return [
    { id: 'monster', emoji: '🐉', name: 'Monster Quest', ...calc(mon, (cls) => classCount(cls), (c) => c) },
    { id: 'carried', emoji: '🤝', name: 'Carried by Monsters', ...calc(mon, (cls) => car[cls] || 0, (c) => c) },
    { id: 'human', emoji: '🗡️', name: 'Human Quest', ...calc(hum, (cls) => classCount(cls), (c) => c) },
    { id: 'everything', emoji: '🌍', name: 'Everything Quest', ...calc([...hum, ...mon], (cls) => classCount(cls), (c) => c) },
    { id: 'gendered', emoji: '⚥', name: 'Human Gendered', ...calc(gendered, (t) => genderCount(t.cls, t.idx), gLabel), sub: [{ label: '♂ Male-only', ...maleProg }, { label: '♀ Female-only', ...femaleProg }] },
    { id: 'bagel', emoji: '🥯', name: 'Everything Bagel', ...calc([...gendered, ...mon.map((c) => ({ cls: c, idx: 0 }))], (t) => t.idx === 0 ? classCount(t.cls) : genderCount(t.cls, t.idx), gLabel) },
    { id: 'zodiac', emoji: '⭐', name: 'Zodiac Quest', ...calc(ZODIAC_SIGNS, (sign) => cs[sign] || 0, (s) => s) },
  ];
}

// "Carried by Monsters": teammate-owned monsters on this player's title teams (their own
// monster excluded), as { monster: timesCarried }, counted once per reign like the leaderboard.
export function carriedByMonsters(champHistory, me, monsterClasses) {
  const tset = new Set(monsterClasses), out = {};
  for (const i of (champHistory.p[me] || [])) {
    const units = champHistory.r[i][3], seen = new Set();
    for (const tm of units) { if (tm[0] === me || tm[2] !== 'X' || !tset.has(tm[1])) continue; seen.add(tm[1]); }
    for (const m of seen) out[m] = (out[m] || 0) + 1;
  }
  return out;
}

// The saved copy the bracket widget shows, or null when there's no goal or it no longer
// exists. A quest lists what's left for its first completion, or for the next full set
// once it has been completed.
export function goalSnapshot(goal, quests, achievements) {
  if (!goal) return null;
  if (goal.type === 'quest') {
    const q = quests.find((x) => x.id === goal.id);
    return q ? { name: q.emoji + ' ' + q.name, remaining: q.remaining.length ? q.remaining : q.remainingNext, status: q.championed + '/' + q.total } : null;
  }
  const a = achievements.find((x) => x.label === goal.id);
  return a ? { name: a.emoji + ' ' + a.label, remaining: [], status: a.status } : null;
}
